import React, { useState, useEffect } from "react";
import { FiSearch } from "react-icons/fi";
import CustomButton from "../components/CustomButton";
import ResearchPaperCard from "../components/cards/ResearchPaperCard";
import Pagination from "../components/ui/Pagination";
import { searchResearchPapers } from "../api/Api";
import DropdownInput from "../components/Inputs/DropdownInput";

function ExploreResearchPapers() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    publicationYear: "",
    institution: "",
    author: "",
    workType: "",
  });

  const [yearOptions, setYearOptions] = useState([]);
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [authorOptions, setAuthorOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);

  const groupByFields = {
    publicationYear: "publication_year",
    institution: "authorships.institutions.lineage",
    author: "authorships.author.id",
    workType: "type",
  };

  const buildFilterString = () => {
    const filtersArr = [];
    if (searchQuery)
      filtersArr.push(`title_and_abstract.search:${searchQuery}`);
    if (filters.publicationYear)
      filtersArr.push(`publication_year:${filters.publicationYear}`);
    if (filters.institution)
      filtersArr.push(
        `authorships.institutions.lineage:${filters.institution}`
      );
    if (filters.author)
      filtersArr.push(`authorships.author.id:${filters.author}`);
    if (filters.workType) filtersArr.push(`type:${filters.workType}`);
    return filtersArr.join(",");
  };

  const fetchGroupByOptions = async (facet, filterString) => {
    try {
      const response = await searchResearchPapers(
        searchQuery,
        1,
        200, //Set to APi's default value
        null,
        null,
        null,
        null,
        groupByFields[facet],
        filterString
      );
      return response.group_by || [];
    } catch {
      return [];
    }
  };

  const mapFilterOptions = (options) =>
    options.map((opt) => ({
      id: opt.key.replace?.("https://openalex.org/", "") || opt.key,
      name: `${opt.key_display_name || opt.key} (${opt.count})`,
    }));
  const clearFilters = () => {
    setFilters({
      publicationYear: "",
      institution: "",
      author: "",
      workType: "",
    });
  };

  const fetchAllFilterOptions = async () => {
    const filterString = buildFilterString();
    const [years, institutions, authors, types] = await Promise.all([
      fetchGroupByOptions("publicationYear", filterString),
      fetchGroupByOptions("institution", filterString),
      fetchGroupByOptions("author", filterString),
      fetchGroupByOptions("workType", filterString),
    ]);
    setYearOptions(mapFilterOptions(years));
    setInstitutionOptions(mapFilterOptions(institutions));
    setAuthorOptions(mapFilterOptions(authors));
    setTypeOptions(mapFilterOptions(types));
  };

  const handleSearch = async (page = 1) => {
    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    try {
      const response = await searchResearchPapers(
        searchQuery,
        page,
        25,
        filters.publicationYear,
        filters.institution,
        filters.author,
        filters.workType,
        null,
        buildFilterString()
      );
      setSearchResults(response.results || []);
      setPagination(response.pagination);
      setCurrentPage(page);
      fetchAllFilterOptions();
    } catch (err) {
      setError("Failed to search papers. Please try again.");
      setSearchResults([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch(1);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (hasSearched) {
      handleSearch(1);
    }
  }, [
    filters.publicationYear,
    filters.institution,
    filters.author,
    filters.workType,
  ]);

  const handleViewPaper = (paper) => {
    console.log("Viewing paper:", paper);
    if (paper.fulltext_link) {
      window.open(paper.fulltext_link, "_blank");
    }
  };

  const handlePageChange = (newPage) => {
    handleSearch(newPage);
  };

  return (
    <div className="p-4 px-xl-5 position-relative">
      {/* Header Section */}
      <div className="mb-4">
        <h5 className="fw-bold mb-3">Explore Research Papers</h5>
        <p className="text-muted p4 m-0">
          Search through millions of academic papers and discover relevant
          research in your field.
          <br />
          Powered by{" "}
          <span className="text-primary text-decoration-none">OpenAlex</span>,
          the comprehensive database of scholarly works.
        </p>
      </div>

      {/* Search Box */}
      <div className="rounded-3 p-3 p-xl-4 overflow-hidden primary-box mb-4">
        <h6 className="bg-white mb-3">Search Research Papers</h6>
        <p className="p4 mb-3 text-secondary">
          Enter keywords, topics, author names, or paper titles to find relevant
          research
        </p>
        <div className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center rounded-1 border p-2 flex-grow-1">
            {" "}
            {/* Changed border radius to smaller */}
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="e.g., Machine Learning, Climate Change, Quantum Computing..."
              className="flex-grow-1 border-0 no-focus-outline p-0 ms-2"
            />
          </div>
          <CustomButton
            icon={<FiSearch />}
            className="rounded-md  px-3 py-2"
            onClick={() => handleSearch(1)}
            disabled={isLoading}
          >
            {isLoading ? "Searching..." : "Search"}
          </CustomButton>
        </div>
        {/* Filter Controls - only show after search */}
        {hasSearched && (
          <div className="mt-4">
            <div className="d-flex align-items-end gap-2">
              <div className="flex-grow-1">
                <div className="row g-2">
                  <div className="col-3">
                    <DropdownInput
                      label="Publication Year"
                      name="publicationYear"
                      options={yearOptions}
                      value={filters.publicationYear}
                      onChange={handleFilterChange}
                      placeholder="Select year"
                      fullWidth
                    />
                  </div>
                  <div className="col-3">
                    <DropdownInput
                      label="Institution"
                      name="institution"
                      options={institutionOptions}
                      value={filters.institution}
                      onChange={handleFilterChange}
                      placeholder="Select Institution"
                      fullWidth
                    />
                  </div>
                  <div className="col-3">
                    <DropdownInput
                      label="Author"
                      name="author"
                      options={authorOptions}
                      value={filters.author}
                      onChange={handleFilterChange}
                      placeholder="Select Author"
                      fullWidth
                    />
                  </div>
                  <div className="col-3">
                    <DropdownInput
                      label="Work Type"
                      name="workType"
                      options={typeOptions}
                      value={filters.workType}
                      onChange={handleFilterChange}
                      placeholder="Select type"
                      fullWidth
                    />
                  </div>
                </div>
              </div>
              <CustomButton
                variant="outline"
                className="btn-sm px-3 py-2 d-flex align-items-center justify-content-center gap-2"
                onClick={() => {
                  clearFilters();
                }}
              >
                Clear All
              </CustomButton>
            </div>
          </div>
        )}
      </div>

      {/* Search Results Section */}
      {hasSearched && (
        <div>
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-secondary">
                Searching research papers...
              </p>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              <h6 className="alert-heading">Search Error</h6>
              <p className="mb-0">{error}</p>
            </div>
          ) : searchResults.length > 0 ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold mb-0">
                  Search Results ({pagination?.total_items} papers found)
                </h5>
                {pagination &&
                  pagination.total_results > pagination.per_page && (
                    <small className="text-secondary">
                      Page {pagination.page} of{" "}
                      {Math.ceil(
                        pagination.total_results / pagination.per_page
                      )}
                    </small>
                  )}
              </div>

              {searchResults.map((paper, index) => {
                // Debug: Log each paper to see its structure
                console.log(`Paper ${index}:`, paper);
                console.log(`Paper keys:`, Object.keys(paper || {}));

                return (
                  <ResearchPaperCard
                    key={`${paper.display_name || paper.title || index}`}
                    paper={{
                      title: paper.display_name || "Unknown Title",
                      authors: Array.isArray(paper.authorship)
                        ? paper.authorship
                            .map((auth) => auth.author?.display_name || auth)
                            .join(", ")
                        : paper.authorship ||
                          paper.authors ||
                          "Unknown Authors",
                      year: paper.publish_year || "Unknown Year",
                      journal: paper.journal || "Unknown Journal",
                      citations: paper.all_citations || 0,
                      summary: paper.primary_topic || "No topic available",
                      fulltext_link: paper.fulltext_link,
                      institutions: paper.institutions,
                    }}
                    onView={handleViewPaper}
                  />
                );
              })}

              {pagination && pagination.total_items > 25 && (
                <Pagination
                  currentPage={pagination.current_page}
                  totalItems={pagination.total_items}
                  pageSize={25}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          ) : (
            <div className="text-center py-5">
              <div
                className="mb-4"
                style={{ fontSize: "3rem", color: "#6c757d" }}
              >
                <FiSearch />
              </div>
              <h6 className="fw-bold mb-3">No results found</h6>
              <p className="text-secondary">
                Try different keywords or check your spelling.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!hasSearched && (
        <div className="text-center py-5">
          <div className="mb-4" style={{ fontSize: "4rem", color: "#6c757d" }}>
            <FiSearch />
          </div>
          <h5 className="fw-bold mb-3">Ready to explore research?</h5>
          <p
            className="text-secondary mb-4 mx-auto"
            style={{ maxWidth: "400px" }}
          >
            Enter your search terms above to discover relevant academic papers,
            track citations, and stay updated with the latest research in your
            field.
          </p>
          <strong>Tip:</strong> Try searching for specific topics, author names,
          or paper titles for the best results.
        </div>
      )}
    </div>
  );
}

export default ExploreResearchPapers;
